<script lang="ts">
import { ref } from "vue";

const openPopovers = ref<symbol[]>([]);
</script>

<script setup lang="ts">
import { X } from "@lucide/vue";
import { computed, onBeforeUnmount } from "vue";
import BasePopover from "@/client/components/BasePopover.vue";

const props = withDefaults(
  defineProps<{
    popoverId: string;
    title: string;
    subtitle?: string;
    anchorName?: string;
    closeLabel?: string;
  }>(),
  {
    subtitle: undefined,
    anchorName: undefined,
    closeLabel: "Close popover",
  },
);

const emit = defineEmits<{
  toggle: [event: Event];
}>();

const popoverElement = ref<InstanceType<typeof BasePopover> | null>(null);
const stackId = Symbol();
const stackOffset = computed(() => `${Math.max(0, openPopovers.value.indexOf(stackId)) * 6}px`);

function removeFromStack(): void {
  openPopovers.value = openPopovers.value.filter((id) => id !== stackId);
}

function onToggle(event: Event): void {
  removeFromStack();
  if ((event as ToggleEvent).newState === "open") {
    openPopovers.value.push(stackId);
  }
  emit("toggle", event);
}

onBeforeUnmount(removeFromStack);
</script>

<template>
  <BasePopover
    :id="props.popoverId"
    ref="popoverElement"
    class="full-popover"
    :style="{
      'position-anchor': props.anchorName,
      '--full-popover-stack-offset': stackOffset,
    }"
    @toggle="onToggle"
  >
    <header class="full-popover__header">
      <div class="full-popover__heading">
        <slot name="heading">
          <div class="full-popover__title">{{ props.title }}</div>
        </slot>
        <div v-if="props.subtitle" class="full-popover__subtitle">{{ props.subtitle }}</div>
      </div>
      <div class="full-popover__header-actions">
        <slot name="header-actions" />
        <button
          type="button"
          class="full-popover__close chrome-button chrome-button--icon"
          :aria-label="props.closeLabel"
          title="Close"
          @click="popoverElement?.hidePopover()"
        >
          <X :size="16" />
        </button>
      </div>
      <div v-if="$slots['header-content']" class="full-popover__header-content">
        <slot name="header-content" />
      </div>
    </header>
    <div class="full-popover__body">
      <slot />
    </div>
  </BasePopover>
</template>

<style scoped>
:global(.full-popover) {
  inset: calc(var(--safe-area-top) + 1rem + var(--full-popover-stack-offset))
    calc(var(--safe-area-right) + 1rem) calc(var(--safe-area-bottom) + 1rem)
    calc(var(--safe-area-left) + 1rem);
  width: auto;
  max-width: none;
  height: auto;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--color-chrome-border);
  border-radius: 1.1rem;
  background: var(--color-bg-panel);
  color: var(--color-text);
  box-shadow: var(--color-shadow-popover);
  overflow: hidden;
}

:global(.full-popover:popover-open) {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
}

:global(.full-popover::backdrop) {
  background: rgb(0 0 0 / 0.22);
}

.full-popover__header {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem 1rem;
  padding: 0.65rem 1rem;
  border-bottom: 1px solid var(--color-chrome-border);
  background: var(--color-bg-instance);
  box-shadow: var(--color-shadow-header);
}

.full-popover__header:has(.full-popover__header-content) {
  row-gap: 0.25rem;
  padding-bottom: 0;
}

.full-popover__heading {
  min-width: 0;
}

.full-popover__title {
  font-size: 1.08rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--color-text-strong);
}

.full-popover__subtitle {
  overflow: hidden;
  color: var(--color-text-subtle);
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.full-popover__header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex-shrink: 0;
}

.full-popover__header-content {
  grid-column: 1 / -1;
  min-width: 0;
  margin-inline: -1rem;
}

.full-popover__body {
  min-height: 0;
  overflow: hidden;
}
</style>
